import ScrollReveal from "@/components/base/ScrollReveal";
import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";

export default function About() {
  const location = useLocation();

  useEffect(() => {
    const scrollTo = (location.state as { scrollTo?: string })?.scrollTo;
    if (scrollTo) {
      const el = document.getElementById(scrollTo);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  }, [location]);

  return (
    <main>
      {/* Schema.org Person JSON-LD for Sue Murray */}
      <script type="application/ld+json">
        {`
        {
          "@context": "https://schema.org",
          "@type": "Person",
          "name": "Sue Murray",
          "jobTitle": "Founder and Principal Advisor",
          "worksFor": {
            "@type": "ProfessionalService",
            "name": "VantageSight AI Advisory LLC",
            "url": "https://vantagesight.readdy.co/"
          },
          "description": "Sue Murray brings 37 years of experience in enterprise technology adoption, with deep expertise in AI strategy, roadmapping, governance, adoption, and workforce planning.",
          "knowsAbout": [
            "AI governance consulting",
            "AI strategy advisory",
            "responsible AI adoption",
            "AI workforce readiness",
            "AI adoption strategy",
            "enterprise AI governance",
            "AI governance setup",
            "government AI advisory",
            "AI readiness assessment",
            "trustworthy AI principles",
            "AI roadmap development",
            "AI strategy development",
            "organizational change management"
          ],
          "url": "https://vantagesight.readdy.co/about",
          "image": "https://static.readdy.ai/image/ff63a95a3d068db59710e28833339873/9db881c9e4333cfdf3dd5df89feaea18.png"
        }
        `}
      </script>

      {/* Hero with background visual */}
      <section id="about-vantage" className="relative bg-bg-section pt-28 pb-16 md:pt-36 md:pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20minimalist%20dark%20blue%20background%20with%20subtle%20geometric%20patterns%20soft%20glowing%20lines%20modern%20professional%20elegant%20corporate%20aesthetic%20deep%20navy%20tones%20artistic&width=1920&height=600&seq=about-hero-bg&orientation=landscape"
            alt=""
            className="w-full h-full object-cover opacity-30"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#07111F]/80 via-[#07111F]/90 to-[#07111F]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent" />
        <div className="absolute top-1/2 -right-32 w-[400px] h-[400px] bg-primary/5 rounded-full blur-[120px]" />

        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
          <ScrollReveal>
            <div className="max-w-4xl mx-auto text-center">
              <span className="font-sans text-[14px] font-medium text-primary tracking-wider uppercase">
                About VantageSight
              </span>
              <h1 className="font-serif text-[36px] md:text-[52px] lg:text-[60px] font-semibold text-text-headline leading-tight mt-4 mb-6">
                Built on <span className="italic text-primary">Experience</span>,{" "}
                Guided by Integrity
              </h1>
              <p className="font-sans text-[17px] md:text-[20px] text-text-muted leading-relaxed max-w-2xl mx-auto">
                VantageSight exists because organizations deserve independent, practical AI advice — honest guidance on governance, strategy, adoption, and workforce readiness from someone who understands both public and private sector realities.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* About Sue with enhanced visuals */}
      <section className="relative bg-bg py-20 md:py-28 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent" />
        <div className="absolute top-1/3 -left-48 w-[400px] h-[400px] bg-primary/3 rounded-full blur-[120px]" />

        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14 items-start">
            <ScrollReveal className="lg:col-span-2" direction="left" delay={100}>
              <div className="relative group">
                <div className="absolute -inset-4 bg-gradient-to-br from-primary/15 via-primary/5 to-transparent rounded-[28px] blur-2xl opacity-60 group-hover:opacity-100 transition-opacity duration-700" />
                <div className="relative w-full aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="https://static.readdy.ai/image/ff63a95a3d068db59710e28833339873/9db881c9e4333cfdf3dd5df89feaea18.png"
                    alt="Sue Murray - VantageSight Founder"
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.03]"
                  />
                </div>
                <div className="mt-6">
                  <h3 className="font-sans text-[24px] font-bold text-text-headline">
                    Sue Murray
                  </h3>
                  <p className="font-sans text-[16px] font-medium text-primary mt-1">
                    Founder and Principal Advisor
                  </p>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal className="lg:col-span-3" direction="right" delay={200}>
              <div className="flex flex-col gap-5 font-sans text-[16px] text-text-muted leading-[1.7]">
                <p>
                  I am Sue Murray, founder of VantageSight AI Advisory LLC. For 37 years, I have led private and public sector organizations through successful technology adoption and change implementation — turning ideas and goals into real impact.
                </p>
                <p>
                  I specialize in guiding leaders and teams through the strategic AI journey: from governance structure and strategy development through workforce readiness and sustained adoption. My deep expertise spans AI strategy, governance, workforce readiness, adoption, process improvement, and organizational change management (OCM).
                </p>
                <p>
                  I do not build AI systems, conduct technical implementation, sell software, or recommend specific AI tools. My role is a trusted independent advisor, providing clarity, focus, guidance, recommendations, and acceleration informed by deep experience and expertise from working with a variety of types of organizations. I help leaders and teams make smart decisions and build the structures needed to follow through.
                </p>
                <p>
                  My clients include private sector companies, government agencies, academic and nonprofit institutions, and enterprise teams. What unites them is a need for clarity and practical direction — not hype, not complexity.
                </p>
                <p>
                  Whether you are a private sector company, a government agency, an academic or nonprofit institution, or an enterprise team, I meet you where you are — with honest conversations, practical advice, and hands-on support that drives real results.
                </p>
              </div>

              <div className="mt-10 p-6 bg-bg-card border border-border rounded-xl relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-primary/50 to-transparent" />
                <p className="font-serif text-[20px] md:text-[22px] text-text-headline italic leading-relaxed pl-4">
                  &ldquo;My job is not to impress you with jargon. It is to help you move forward with confidence.&rdquo;
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Values with enhanced visuals */}
      <section className="relative bg-bg-section py-20 md:py-28 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent" />
        <div className="absolute bottom-1/4 -right-32 w-[400px] h-[400px] bg-primary/3 rounded-full blur-[120px]" />

        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <ScrollReveal>
              <h2 className="font-serif text-[32px] md:text-[40px] font-semibold text-text-headline text-center mb-14">
                What Guides My Work
              </h2>
            </ScrollReveal>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  icon: "ri-eye-line",
                  title: "Clarity First",
                  desc: "Every recommendation starts with understanding your organization's situation deeply. No assumptions, no shortcuts, no generic templates.",
                },
                {
                  icon: "ri-heart-line",
                  title: "Honest Partnership",
                  desc: "I tell you what you need to hear, not what you want to hear. Your trust is more valuable than any single engagement.",
                },
                {
                  icon: "ri-seedling-line",
                  title: "Practical Growth",
                  desc: "Small steps executed well beat grand plans that never launch. I focus on approaches your team can actually implement.",
                },
              ].map((v, i) => (
                <ScrollReveal key={i} delay={i * 150} direction="up">
                  <div className="bg-bg-card border border-border rounded-2xl p-8 md:p-10 transition-all duration-300 hover:border-primary/40 hover:-translate-y-1 group relative overflow-hidden h-full">
                    <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="relative z-10">
                      <div className="w-12 h-12 bg-primary-tint rounded-full flex items-center justify-center mb-5 transition-all duration-300 group-hover:bg-primary/15 group-hover:scale-110">
                        <i className={`${v.icon} text-primary text-xl w-6 h-6 flex items-center justify-center`} />
                      </div>
                      <h3 className="font-sans text-[20px] font-semibold text-text-headline mb-3 group-hover:text-primary transition-colors duration-300">
                        {v.title}
                      </h3>
                      <p className="font-sans text-[15px] text-text-muted leading-relaxed">
                        {v.desc}
                      </p>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-bg py-20 md:py-28 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent" />
        <div className="absolute top-1/2 -left-48 w-[300px] h-[300px] bg-primary/3 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 -right-48 w-[300px] h-[300px] bg-primary/3 rounded-full blur-[120px]" />

        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
          <ScrollReveal>
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-serif text-[32px] md:text-[40px] font-semibold text-text-headline mb-6">
                Ready to Work Together?
              </h2>
              <p className="font-sans text-[17px] md:text-[18px] text-text-muted leading-relaxed mb-8">
                I work with a limited number of clients at a time to ensure every engagement gets the attention it deserves. If your organization needs governance, strategy, workforce planning, assessment design, workshop design, or independent review, the best next step is a conversation.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/contact"
                  className="font-sans text-[16px] font-medium text-white bg-primary hover:bg-primary-light px-8 py-4 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer hover:scale-105 inline-block"
                >
                  Schedule a 30-Minute Consultation
                </Link>
                <a
                  href="mailto:smurray@vantagesightai.com"
                  className="font-sans text-[16px] font-medium text-primary border-2 border-primary hover:bg-primary hover:text-white px-8 py-4 rounded-full transition-all duration-300 whitespace-nowrap hover:scale-105"
                >
                  Send an Email
                </a>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </main>
  );
}